import { ChangeDetectorRef, HostListener, TrackByFunction, Injectable } from '@angular/core';
import { ColumnOptions } from '@core';
import { TranscationDetailKeyKownService } from '@shared-feature/services/transcation-detail-key-kown.service';
import { debounceTime } from 'rxjs/operators';

// Interface to define the minimum required methods for dataSrv
export interface IPaginatedTableDataService {
  setComponentRef(component: any): void;
  parentComponent: any;
  newLineAdded$: any;
  itemDetails: any;
  dataInitialized: any;
  addLines(count?: number): void;
  removeLine(formArray: any, index: number): void;
  dropLine(event: any): void;
}

@Injectable()
export abstract class PaginatedTableOptimizedComponent<T extends IPaginatedTableDataService = IPaginatedTableDataService> {
  selectComponentArray: any;
  pageSize: number = 10;
  currentPage: number = 1;
  editableCells: any;
  newlyAddedLine: boolean = false;
  visibleColumns: ColumnOptions[] = [];
  paginatedData: any[] = []; // Paginated data - updated only when needed

  private _pageNumbers: Array<number | string> = []; // Cached page numbers - updated only when needed
  private _paginationSummary: string = '';
  private _totalRowCount: number = 0;
  private _totalPages: number = 0;
  
  private _lastDataLength: number = 0;
  private _lastTotalPages: number = 0;
  private _lastCurrentPage: number = 1;
  
  protected dataSrv: T;
  protected cdr: ChangeDetectorRef;
  public keyDownService: TranscationDetailKeyKownService;

  constructor(
    dataSrv: T,
    cdr: ChangeDetectorRef,
    keyDownService: TranscationDetailKeyKownService,
  ) {
    this.dataSrv = dataSrv;
    this.cdr = cdr;
    this.keyDownService = keyDownService;
    this.dataSrv.setComponentRef(this);
    this.dataSrv.parentComponent = this;

    this.dataSrv.newLineAdded$.subscribe(() => {
      this.newlyAddedLine = true;
    });

    // Subscribe to data initialization to ensure pagination updates after setData
    // This is especially important when the length doesn't change (e.g., 1 row -> 1 row)
    if (this.dataSrv.dataInitialized) {
      this.dataSrv.dataInitialized.subscribe(() => {
        // Update paginated data after data is loaded, even if length didn't change
        this._lastDataLength = this.dataSrv.itemDetails?.length ?? 0;
        this.updatePaginatedData();
      });
    }

    // Subscribe to form array length changes to update paginated data when data loads
    // Use debounce to batch multiple rapid updates (e.g., when loading multiple items)
    if (this.dataSrv.itemDetails) {
      this._lastDataLength = this.dataSrv.itemDetails.length;
      this.dataSrv.itemDetails.valueChanges
        .pipe(
          debounceTime(0) // Increased debounce to reduce frequent updates during scrolling/typing
        )
        .subscribe(() => {
          const currentLength = this.dataSrv.itemDetails.length;
          // Only update if the array length changed (data loaded, items added/removed)
          if (currentLength !== this._lastDataLength) {
            // Reset to page 1 when data loads (length changes from 0 to >0)
            if (this._lastDataLength === 0 && currentLength > 0) {
              this.currentPage = 1;
            }
            this._lastDataLength = currentLength;
            this.updatePaginatedData();
          }
        });
    }

    // Initialize paginated data
    this.updatePaginatedData();
  }

@HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent, globalRowIndex: number, columnName: string): void {
    // Early return for non-relevant keys to avoid expensive DOM queries
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(event.key)) {
      return;
    }
 
    // Cache DOM query result - only query when needed
    // Get the elements with the class 'dropdownSelect' to check if a dropdown is open
    const isSelectOpen = Array.from(document.getElementsByClassName('ant-select-dropdown'))
      .filter((element) => 
        !element.classList.contains('ant-select-dropdown-hidden') &&
        element.getAttribute('ng-reflect-open') !== 'false'
      );
    
    // Update the column order based on the current page
    this.keyDownService.updateColumnOrder(this.visibleColumns);

    /**
     * This block handles the keydown events when the dropdown is not open.
     * If the dropdown is open or has options, the select component will handle its own keydown events.
     *
     * isSelectOpen.length = 0 indicates there is no dropdown.
     * isSelectOpen.length = 1 indicates there is a dropdown, but it does not have an option list to interact with.
     * In both cases, we want to handle the keydown event in this method.
     */
    if (
      ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(
        event.key
      ) &&
      isSelectOpen.length <= 1
    ) {
      let nextGlobalRowIndex = globalRowIndex;
      let nextColumnName = columnName;

      switch (event.key) {
        case 'ArrowDown':
          // Move to the next row
          if (isSelectOpen.length <= 1) nextGlobalRowIndex++;
          break;

        case 'ArrowUp':
          // Move to the previous row
          if (isSelectOpen.length <= 1) nextGlobalRowIndex--;
          break;

        case 'ArrowLeft':
          if (!this.selectComponentArray.includes(columnName)) {
            const element = document.activeElement as HTMLInputElement;

            if (element.selectionStart === 0) {
              // Move to the next column
              nextColumnName = this.keyDownService.getAdjacentColumn(
                columnName,
                -1
              );
            }
          } else {
            nextColumnName = this.keyDownService.getAdjacentColumn(
              columnName,
              -1
            );
          }
          break;
        case 'ArrowRight':
          if (!this.selectComponentArray.includes(columnName)) {
            const element = document.activeElement as HTMLInputElement;

            if (element.selectionEnd === element.value.length) {
              // Move to the next column
              nextColumnName = this.keyDownService.getAdjacentColumn(
                columnName,
                1
              );
            }
          } else {
            nextColumnName = this.keyDownService.getAdjacentColumn(
              columnName,
              1
            );
          }
          break;

        case 'Enter':
          // Handle the Enter key press
          if (event.ctrlKey) {
            // If Ctrl + Enter is pressed, add a new line and focus on the new line's select component
            this.addLines();
          } else if (!this.selectComponentArray.includes(columnName)) {
            /**
             * If Enter is pressed and the column is not a custom select component,
             * move to the next cell in the same row.
             * Custom select components handle their own navigation through `dataSrv.lineValueChanged`.
             */
            nextColumnName = this.keyDownService.getAdjacentColumn(
              columnName,
              1
            );
          }
          break;
      }

      // If the new row index is valid, move focus to the new cell
      if (
        this.keyDownService.isRowIndexValid(
          nextGlobalRowIndex,
          this.dataSrv.itemDetails.length
        )
      ) {
        // Calculate which page the target row is on
        const targetPage = Math.floor(nextGlobalRowIndex / this.pageSize) + 1;
        
        // If the target row is on a different page, navigate to that page first
        if (targetPage !== this.currentPage) {
          this.currentPage = targetPage;
         this.updatePaginatedData(); 
          
          // After page change, focus on the correct cell using local page index
          setTimeout(() => {
            const localRowIndex = nextGlobalRowIndex % this.pageSize;
            this.keyDownService.focusNextCell(
              this.editableCells.toArray(),
              localRowIndex,
              nextColumnName
            );
          }, 100); // Small delay to ensure the new page is rendered
        } else {
          // If on the same page, use local row index
          const localRowIndex = nextGlobalRowIndex % this.pageSize;
          this.keyDownService.focusNextCell(
            this.editableCells.toArray(),
            localRowIndex,
            nextColumnName
          );
        }
      }
    }
  }


  addLines() {
    this.dataSrv.addLines();
    this.goToPage(this._totalPages)
 }

  // Pagination methods
  get totalRowCount(): number {
    return this._totalRowCount;
  }

  get totalPages(): number {
    return this._totalPages;
  }

  get pageNumbers(): Array<number | string> {
    return this._pageNumbers;
  }

  get paginationSummary(): string {
    return this._paginationSummary;
  }

  trackByRow: TrackByFunction<any> = (index: number, item: any): any => {
    return item;
  };

  getPaginatedData(): any[] {
    return this.paginatedData;
  }

  updatePaginatedData(): void {
    const controls = this.dataSrv.itemDetails?.controls || [];
    
    // Update total counts
    this._totalRowCount = controls.length;
    this._totalPages = Math.ceil(this._totalRowCount / this.pageSize);
    
    if (controls.length === 0) {
      this.paginatedData = [];
      this._pageNumbers = [];
      this._paginationSummary = '0 - 0 of 0';
      return;
    }

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    // Slice the controls and enrich each item with its global index
    const slicedControls = controls.slice(startIndex, endIndex);
    this.paginatedData = slicedControls.map((control: any, localIndex: number) => {
      const globalIndex = startIndex + localIndex;
      // Attach globalIndex to the control for easy access in template
      if (!control.globalIndex || control.globalIndex !== globalIndex) {
        control.globalIndex = globalIndex;
      }
      return control;
    });
    
    // Update page numbers cache
    this.updatePageNumbersCache();
    
    // Update pagination summary cache
    this.updatePaginationSummaryCache();
  }

  getGlobalIndex(pageIndex: number): number {
    // This method is kept for backward compatibility and programmatic use
    // In templates, use line.globalIndex instead for better performance
    return (this.currentPage - 1) * this.pageSize + pageIndex;
  }

  getPageNumbers(): Array<number | string> {
    return this._pageNumbers;
  }

  getPaginationSummary(): string {
    return this._paginationSummary;
  }

  private updatePageNumbersCache(): void {
    const totalPages = this._totalPages;
    const currentPage = this.currentPage;

    // Only update if totalPages or currentPage changed
    if (totalPages === this._lastTotalPages && currentPage === this._lastCurrentPage) {
      return;
    }

    this._lastTotalPages = totalPages;
    this._lastCurrentPage = currentPage;

    if (totalPages <= 7) {
      this._pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
      return;
    }

    const pages: Array<number | string> = [];

    if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }

    this._pageNumbers = pages;
  }

  private updatePaginationSummaryCache(): void {
    const startItem = (this.currentPage - 1) * this.pageSize + 1;
    const endItem = Math.min(this.currentPage * this.pageSize, this._totalRowCount);
    this._paginationSummary = `${startItem} - ${endItem} of ${this._totalRowCount}`;
  }

  nextPage() {
    if (this.currentPage < this._totalPages) {
      this.currentPage++;
      this.updatePaginatedData();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedData();
    }
  }


  onPageSizeChange(newPageSize: any): void {
    let actualPageSize: number;

    if (typeof newPageSize === 'object') {
      actualPageSize = newPageSize.value || newPageSize;
    } else if (typeof newPageSize === 'string') {
      actualPageSize = parseInt(newPageSize, 10);
    } else {
      actualPageSize = newPageSize;
    }
  }

  removeLineAtGlobalIndex(globalIndex: number) {
    this.dataSrv.removeLine(this.dataSrv.itemDetails, globalIndex);
    this.dataSrv.itemDetails.updateValueAndValidity();

    const totalRows = this.dataSrv.itemDetails?.controls?.length || 0;
    const totalPages = Math.ceil(totalRows / this.pageSize);

    if (this.currentPage > 1 && this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }

    if (this.currentPage < 1) {
      this.currentPage = 1;
    }

    // Force immediate UI refresh (OnPush + nz-table relies on nzData reference updates)
    this._lastDataLength = totalRows;
    this.updatePaginatedData();
    this.cdr.markForCheck();
  }

 goToPage(page: number) {
    if (page >= 1 && page <= this._totalPages) {
      this.currentPage = page;
      this.updatePaginatedData();
    }
  }

  onDropLine(event: any) {
    // Convert local page indices to global indices
    const globalPreviousIndex = (this.currentPage - 1) * this.pageSize + event.previousIndex;
    const globalCurrentIndex = (this.currentPage - 1) * this.pageSize + event.currentIndex;
    
    // Create a modified event with global indices
    const modifiedEvent = {
      ...event,
      previousIndex: globalPreviousIndex,
      currentIndex: globalCurrentIndex,
      container: {
        ...event.container,
        data: this.dataSrv.itemDetails // Use the full array, not paginated data
      }
    };
    
    // Call the original dropLine method with the modified event
    this.dataSrv.dropLine(modifiedEvent);
    this.updatePaginatedData();
  }
}
